import {
  useDistrictsQuery,
  useProvincesQuery,
} from "@/modules/geo/presentation/api/queries";
import type {
  OrderDetailDTO,
  OrderUpdateCustomerInfoInput,
} from "@/modules/orders/domain/contracts";
import { useCarriersQuery } from "@/modules/shipping/presentation/api/queries";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Label,
  Select,
  Textarea,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { UserIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useUpdateOrderCustomerInfo } from "../api/queries";
import {
  RETAIL_WALK_IN_SHIPPING_LABEL,
  isRetailWalkInOrder,
} from "../utils/order-retail-display";

type FormState = {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  shippingRecipientName: string;
  shippingPhone: string;
  shippingVillage: string;
  shippingProvinceId: string;
  shippingDistrictId: string;
  shippingCarrierId: string;
  carrierBranch: string;
  shippingZone: string;
};

function formFromOrder(order: OrderDetailDTO): FormState {
  return {
    customerName: order.customerName ?? "",
    customerEmail: order.customerEmail ?? "",
    customerPhone: order.customerPhone ?? "",
    shippingRecipientName: order.shipping?.shippingRecipientName ?? "",
    shippingPhone: order.shipping?.shippingPhone ?? "",
    shippingVillage: order.shipping?.shippingVillage ?? "",
    shippingProvinceId: order.shipping?.shippingProvinceId ?? "",
    shippingDistrictId: order.shipping?.shippingDistrictId ?? "",
    shippingCarrierId: order.shipping?.shippingCarrierId ?? "",
    carrierBranch: order.shipping?.carrierBranch ?? "",
    shippingZone: order.shipping?.shippingZone ?? "",
  };
}

function i18nPick(name: unknown): string {
  if (!name || typeof name !== "object") return "";
  const o = name as Record<string, string>;
  return o.lo ?? o.th ?? o.en ?? "";
}

function buildPayload(
  order: OrderDetailDTO,
  form: FormState,
): OrderUpdateCustomerInfoInput {
  const payload: OrderUpdateCustomerInfoInput = {};
  if (order.customerId) {
    payload.customerName = form.customerName.trim();
    payload.customerEmail = form.customerEmail.trim();
    payload.customerPhone = form.customerPhone.trim()
      ? form.customerPhone.trim()
      : null;
  }
  payload.shippingRecipientName = form.shippingRecipientName.trim();
  payload.shippingPhone = form.shippingPhone.trim()
    ? form.shippingPhone.trim()
    : null;
  payload.shippingVillage = form.shippingVillage.trim();
  payload.shippingProvinceId = form.shippingProvinceId || null;
  payload.shippingDistrictId = form.shippingDistrictId || null;
  payload.shippingZone = form.shippingZone.trim()
    ? form.shippingZone.trim()
    : null;
  payload.shippingCarrierId = form.shippingCarrierId.trim()
    ? form.shippingCarrierId.trim()
    : null;
  payload.carrierBranch = form.carrierBranch.trim()
    ? form.carrierBranch.trim()
    : null;
  return payload;
}

const PRE_SHIP_STATUSES = new Set(["pending_payment", "confirmed", "packing"]);

export function OrderCustomerInfoCard({
  order,
  orderId,
  footer,
  retailWalkIn: retailWalkInProp,
}: {
  order: OrderDetailDTO;
  orderId: string;
  footer: ReactNode;
  retailWalkIn?: boolean;
}) {
  const retailWalkIn =
    retailWalkInProp ?? isRetailWalkInOrder(order);
  const canEdit = PRE_SHIP_STATUSES.has(order.status);
  const updateInfo = useUpdateOrderCustomerInfo(orderId);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(() => formFromOrder(order));

  const provinces = useProvincesQuery({ limit: 500, offset: 0 });
  const provinceRows = provinces.data?.data ?? [];

  const districts = useDistrictsQuery({
    limit: 500,
    offset: 0,
    filters:
      form.shippingProvinceId.length > 0
        ? [
            {
              field: "provinceId",
              op: "eq",
              value: form.shippingProvinceId,
            },
          ]
        : [],
  });
  const districtRows = districts.data?.data ?? [];

  const carriersQuery = useCarriersQuery();
  const carrierRows = carriersQuery.data ?? [];
  const carrierOptions = useMemo(() => {
    const curId = order.shipping?.shippingCarrierId;
    let list = carrierRows.filter((c) => c.enabled);
    if (curId && !list.some((c) => c.id === curId)) {
      const cur = carrierRows.find((c) => c.id === curId);
      if (cur) list = [cur, ...list];
    }
    return [...list].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
  }, [carrierRows, order.shipping?.shippingCarrierId]);

  useEffect(() => {
    if (!editing) {
      setForm(formFromOrder(order));
    }
  }, [order, editing]);

  useEffect(() => {
    if (!form.shippingDistrictId) return;
    if (!districtRows.some((d) => d.id === form.shippingDistrictId)) {
      setForm((f) => ({ ...f, shippingDistrictId: "" }));
    }
  }, [districtRows, form.shippingDistrictId]);

  const provinceOptions = useMemo(
    () =>
      [...provinceRows].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [provinceRows],
  );

  const startEdit = () => {
    setForm(formFromOrder(order));
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(formFromOrder(order));
  };

  const save = async () => {
    if (!form.shippingRecipientName.trim()) {
      toast.error("ກອກຊື່ຜູ້ຮັບ");
      return;
    }
    if (!form.shippingVillage.trim()) {
      toast.error("ກອກບ້ານ / ທີ່ຢູ່");
      return;
    }
    if (order.customerId) {
      if (!form.customerName.trim()) {
        toast.error("ກອກຊື່ລູກຄ້າ");
        return;
      }
      if (!form.customerEmail.trim()) {
        toast.error("ກອກອີເມວ");
        return;
      }
    }
    if (form.shippingCarrierId.trim()) {
      if (!form.carrierBranch.trim()) {
        toast.error("ກະລຸນາປ້ອນສາຂາເມື່ອເລືອກຜູ້ຂົນສົ່ງ");
        return;
      }
    }

    const payload = buildPayload(order, form);
    await updateInfo.mutateAsync(payload);
    setEditing(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <h2 className="font-semibold text-lg">ຂໍ້ມູນລູກຄ້າ</h2>
        <div className="flex items-center gap-2">
          {canEdit && !editing ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={startEdit}
            >
              ແກ້ໄຂ
            </Button>
          ) : null}
          {canEdit && editing ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={cancelEdit}
                disabled={updateInfo.isPending}
              >
                ຍົກເລີກ
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void save()}
                disabled={updateInfo.isPending}
              >
                {updateInfo.isPending ? "…" : "ບັນທຶກ"}
              </Button>
            </>
          ) : null}
          <UserIcon className="size-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {editing ? (
          <div className="space-y-4">
            {!order.customerId ? (
              <p className="rounded-md border border-dashed bg-muted/20 p-2 text-muted-foreground text-xs">
                ອໍເດີນີ້ບໍ່ມີບັນຊີລູກຄ້າ — ປ່ຽນແຕ່ທີ່ຢູ່ຈັດສົ່ງ ແລະ ຊ່ອງຕິດຕໍ່ດ້ານລຸ່ມ
              </p>
            ) : null}
            {order.customerId ? (
              <>
                <div>
                  <Label className="text-xs">ຊື່ລູກຄ້າ (ບັນຊີ)</Label>
                  <Input
                    value={form.customerName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, customerName: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">ອີເມວ</Label>
                  <Input
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, customerEmail: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">ເບີໂທ (ບັນຊີ)</Label>
                  <Input
                    value={form.customerPhone}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        customerPhone: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            ) : null}
            <div className="border-border border-t pt-3">
              <p className="mb-2 font-medium text-foreground text-xs uppercase tracking-wide">
                ທີ່ຢູ່ຈັດສົ່ງ
              </p>
              <div className="grid gap-3">
                <div>
                  <Label className="text-xs">ຊື່ຜູ້ຮັບ</Label>
                  <Input
                    value={form.shippingRecipientName}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        shippingRecipientName: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">ເບີໂທຜູ້ຮັບ</Label>
                  <Input
                    value={form.shippingPhone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, shippingPhone: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <Label className="text-xs">ບ້ານ / ທີ່ຢູ່</Label>
                  <Textarea
                    value={form.shippingVillage}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        shippingVillage: e.target.value,
                      }))
                    }
                    rows={3}
                    className="min-h-[4.5rem] resize-y"
                  />
                </div>
                <div>
                  <Label className="text-xs">ແຂວງ</Label>
                  <Select
                    value={form.shippingProvinceId || "__none__"}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        shippingProvinceId: v === "__none__" ? "" : v,
                        shippingDistrictId: "",
                      }))
                    }
                  >
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue placeholder="ເລືອກແຂວງ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {provinceOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {i18nPick(p.name) || p.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">ເມືອງ</Label>
                  <Select
                    value={form.shippingDistrictId || "__none__"}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        shippingDistrictId: v === "__none__" ? "" : v,
                      }))
                    }
                    disabled={!form.shippingProvinceId}
                  >
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue placeholder="ເລືອກເມືອງ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {districtRows.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {i18nPick(d.name) || d.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">ຜູ້ຂົນສົ່ງ</Label>
                  <Select
                    value={form.shippingCarrierId || "__none__"}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        shippingCarrierId: v === "__none__" ? "" : v,
                        carrierBranch: "",
                      }))
                    }
                  >
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue placeholder="ເລືອກຜູ້ຂົນສົ່ງ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {carrierOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {i18nPick(c.name) || c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">ສາຂາ / ຈຸດຮັບ</Label>
                  <Input
                    value={form.carrierBranch}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, carrierBranch: e.target.value }))
                    }
                    placeholder="ຕົວຢ່າງ ສາຂາສູນກາງ"
                    disabled={!form.shippingCarrierId}
                  />
                </div>
                <div>
                  <Label className="text-xs">ໂຊນຈັດສົ່ງ (ລະຫັດ)</Label>
                  <Input
                    value={form.shippingZone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, shippingZone: e.target.value }))
                    }
                    placeholder="VTE / …"
                    className="font-mono"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div>
              <p className="text-muted-foreground uppercase tracking-wide">
                ຊື່-ນາມສະກຸນ
              </p>
              <p className="font-medium">
                {order.customerName ?? order.customerEmail ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground uppercase tracking-wide">
                ຕິດຕໍ່
              </p>
              <p>
                {order.customerEmail ?? "—"}
                {order.customerPhone && <> • {order.customerPhone}</>}
              </p>
            </div>
            <div className="space-y-3">
              <p className="text-muted-foreground uppercase tracking-wide">
                {retailWalkIn ? "ຮັບສິນຄ້າ" : "ທີ່ຢູ່ຈັດສົ່ງ"}
              </p>
              {retailWalkIn ? (
                <div className="rounded-md border border-dashed bg-muted/15 p-3">
                  <p className="font-medium">{RETAIL_WALK_IN_SHIPPING_LABEL}</p>
                  {order.customerName &&
                  order.customerName !== order.shipping?.shippingRecipientName ? (
                    <p className="mt-1 text-muted-foreground text-xs">
                      ລູກຄ້າ: {order.customerName}
                      {order.customerPhone ? ` · ${order.customerPhone}` : ""}
                    </p>
                  ) : null}
                </div>
              ) : (
              <div className="grid gap-3 font-normal">
                <div>
                  <p className="text-muted-foreground text-xs">ຊື່ຜູ້ຮັບ</p>
                  <p>{order.shipping?.shippingRecipientName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ເບີໂທຜູ້ຮັບ</p>
                  <p>{order.shipping?.shippingPhone ?? "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ບ້ານ / ທີ່ຢູ່</p>
                  <p className="whitespace-pre-line">
                    {order.shipping?.shippingVillage?.trim()
                      ? order.shipping.shippingVillage
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ແຂວງ</p>
                  <p>{order.shipping?.shippingProvinceName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ເມືອງ</p>
                  <p>{order.shipping?.shippingDistrictName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ຜູ້ຂົນສົ່ງ</p>
                  <p>
                    {[
                      order.shipping?.shippingCarrierName,
                      order.shipping?.carrierBranch,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                </div>
                {order.shipping?.shippingZone ? (
                  <div>
                    <p className="text-muted-foreground text-xs">ໂຊນຈັດສົ່ງ</p>
                    <p>{order.shipping.shippingZone}</p>
                  </div>
                ) : null}
              </div>
              )}
            </div>
          </>
        )}
        {footer}
      </CardContent>
    </Card>
  );
}
